import { IOrdersInfoSchema } from './checkpoint-schema.interface';
import { ILocationSchema } from './location-schema.interface';
import { ITripErrors, ITripServiceSchema } from './trip-schema.interface';
import { ITripStopoverInfo } from './trip-table-schema.interface';
import { StepStatusEnum } from '../../enums';

export interface IStepDetailsSchema {
    title: string;
    plannedAt: string;
    triggeredAt: string;
    realEnd: string;
    scheduleEnd: string;
    normalDuration: string;
    duration: string;
    status: StepStatusEnum;
    estimatedAt: string;
    durationDiff: string;
    startDiff: string;
    endDiff: string;
    hasNextStep?: boolean;
    isOrigin?: boolean;
    someCheckpointIsBeenThrough?: boolean;
    realDuration?: string;
    realTimeToNextStep?: string;
    delayTimeToNextStep?: string;
    isNonOperative?: boolean;
    manualTrigger?: boolean;
}

export interface ICheckpointDetailsSchema {
    /** Stable identity within the trip, what an unfold opening refers to. */
    checkpointId?: string;
    checkpointDefinitionId: string;
    type: string;
    name?: string;
    location: ILocationSchema | string;
    inTransit: boolean;
    beenThrough: boolean;
    steps: IStepDetailsSchema[];
    status: string;
    hasNextCheckpoint?: boolean;
    hasPreviousCheckpoint?: boolean;
    isOrigin?: boolean;
    isLastCheckpoint?: boolean;
    tripIsDone?: boolean;
    someCheckpointIsBeenThrough?: boolean;
    ordersInfo?: IOrdersInfoSchema;
    actualPermanence?: string;
    targetPermanence?: string;
    delay?: string;
    isStopover?: boolean;
    isPlanned?: boolean;
    /** Client of the stop, shown as a pill under its title. */
    accountName?: string;
    address?: string;
    latitude?: string;
    longitude?: string;
}

/** One resource of a section, with whether it differs from the section before it. */
export interface ITripSectionResourceSchema {
    label: string;
    changed: boolean;
}

/**
 * A trip section ("tramo") as the detail view needs it: the unit working it, the stops it covers
 * and its own metrics. Derived from the checkpoints — see DESENGANCHE_UNIT_CHANGE_PLAN.md.
 */
export interface ITripSectionSchema {
    number: number;
    total: number;
    startCheckpointIndex: number;
    endCheckpointIndex: number;
    /** No tracking unit assigned yet, so it has no ETA either. */
    isPending: boolean;
    /** completed | in_progress | pending */
    status: string;
    carrier?: ITripSectionResourceSchema;
    vehicle?: ITripSectionResourceSchema;
    driver?: ITripSectionResourceSchema;
    semitrailer?: ITripSectionResourceSchema;
    /** Metres actually travelled inside the section, or the theoretical estimate before it runs. */
    meters?: number;
    /** Whether `meters` is the estimate rather than the travelled distance. */
    metersAreEstimated?: boolean;
    /**
     * When the trip actually changed hands into this section: the incoming unit's arrival at the
     * yard when it was observed there, otherwise the moment the handover was applied. Absent on
     * section 1 and on a handover that has not happened yet.
     */
    handoverAt?: string;
    /** Formatted duration of the section, real once it has run. */
    duration?: string;
    /** Formatted delay accumulated in the section, signed. */
    delay?: string;
}

/** Where the vehicle came from: the closing stop of the last trip it completed. */
export interface ITripPreviousDestinationSchema {
    locationName?: string;
    accountName?: string;
    /** Its last step, so the column can show the same card shape as any other stop. */
    stepTitle?: string;
    realAt?: string;
    plannedAt?: string;
}

/** Where an opening stands: pending until it leaves the hub, which follows the original's departure from it. */
export type TripUnfoldOpeningStatus = 'pending' | 'inProgress' | 'finished';

export interface ITripUnfoldOpeningStopSchema {
    name?: string;
    accountName?: string;
}

/** A trip unfolded from this one, as the unfold screen lists it. */
export interface ITripUnfoldOpeningSchema {
    id: string;
    tripCode: string;
    status: TripUnfoldOpeningStatus;
    scheduledStart?: string;
    driverName?: string;
    vehicleLicensePlate?: string;
    /** Its own checkpoints, the ones after the mirrored hub. */
    stops: ITripUnfoldOpeningStopSchema[];
}

export interface ITripDetailsSchema {
    id: string;
    tripCode?: string;
    /** A `TripTableStatusEnum` value: where the trip is in its lifecycle. */
    status?: string;
    terminalName: string;
    tripDefinitionName?: string;
    services?: ITripServiceSchema[];
    accounts?: string[];
    realEnd: string;
    scheduledStart?: string;
    /** Last destination of the previous trip of this trip's vehicle, rendered as its own column. */
    previousDestination?: ITripPreviousDestinationSchema;
    /**
     * The vehicle was already standing at that destination when this trip started, so both are the
     * same physical event and the timeline merges them.
     */
    startedFromPreviousDestination?: boolean;
    realStart?: string;
    totalTime?: string;
    tags?: string[];
    checkpoints: ICheckpointDetailsSchema[];
    sections?: ITripSectionSchema[];
    errors?: ITripErrors[];
    activeStopover?: ITripStopoverInfo;
    commentsHidden?: boolean;
    /** The trip can still be unfolded into openings. */
    isUnfoldable?: boolean;
    /** Some trip was already unfolded from this one. */
    hasUnfoldOpenings?: boolean;
    /** Type of the checkpoint the trip is unfolded from. Present on every trip that has an unfold config. */
    unfoldHubCheckpointType?: string;
    /** Resolved on demand: it costs one extra trips lookup. */
    unfoldOpenings?: ITripUnfoldOpeningSchema[];
}
